import { z } from 'zod';

/**
 * The player's silhouette: a few named parts, so that it is small to store (the database keeps it
 * under 512 bytes), cheap to check and drawn from code on every screen. Nothing here is an image.
 */

/**
 * Every value a part can have. The first of each list are free for everybody; the last ones are
 * unlocked by playing (`@il-colpo/meta` lists the rule of each, and the progress screens show it).
 */
export const AVATAR_HATS = [
  'fedora',
  'cap',
  'beret',
  'bowler',
  'headphones',
  'none',
  'tophat',
  'panama',
  'hood',
] as const;
export const AVATAR_MASKS = ['none', 'domino', 'bandana', 'visor', 'fox', 'skull'] as const;
export const AVATAR_COATS = [
  'ink',
  'navy',
  'plum',
  'olive',
  'brown',
  'grey',
  'crimson',
  'ivory',
] as const;
export const AVATAR_RIMS = [
  'cyan',
  'amber',
  'green',
  'pink',
  'chalk',
  'violet',
  'gold',
  'ghost',
] as const;
export const AVATAR_SUITS = ['spade', 'heart', 'diamond', 'club', 'star', 'crown'] as const;

export const AvatarSchema = z.object({
  hat: z.enum(AVATAR_HATS).default('fedora'),
  mask: z.enum(AVATAR_MASKS).default('none'),
  coat: z.enum(AVATAR_COATS).default('ink'),
  rim: z.enum(AVATAR_RIMS).default('cyan'),
  suit: z.enum(AVATAR_SUITS).default('spade'),
});
export type Avatar = z.infer<typeof AvatarSchema>;

export type AvatarPart = keyof Avatar;

export const AVATAR_PARTS: { [K in AvatarPart]: readonly Avatar[K][] } = {
  hat: AVATAR_HATS,
  mask: AVATAR_MASKS,
  coat: AVATAR_COATS,
  rim: AVATAR_RIMS,
  suit: AVATAR_SUITS,
};

/** What anybody can wear, with no unlocking: the values a new profile or a bot may be given. */
export const AVATAR_FREE: { [K in AvatarPart]: readonly Avatar[K][] } = {
  hat: ['fedora', 'cap', 'beret', 'bowler', 'headphones', 'none'],
  mask: ['none', 'domino', 'bandana', 'visor'],
  coat: ['ink', 'navy', 'plum', 'olive', 'brown', 'grey'],
  rim: ['cyan', 'amber', 'green', 'pink', 'chalk', 'violet'],
  suit: ['spade', 'heart', 'diamond', 'club', 'star'],
};

export const DEFAULT_AVATAR: Avatar = AvatarSchema.parse({});

/** A silhouette from whatever is stored: unknown parts fall back to the default one by one. */
export function readAvatar(stored: unknown): Avatar {
  const loose = typeof stored === 'object' && stored !== null ? stored : {};
  const out: Record<string, unknown> = {};
  for (const part of Object.keys(AVATAR_PARTS) as AvatarPart[]) {
    const value = (loose as Record<string, unknown>)[part];
    out[part] = (AVATAR_PARTS[part] as readonly unknown[]).includes(value)
      ? value
      : DEFAULT_AVATAR[part];
  }
  return out as Avatar;
}

/** A small number from text (FNV-1a), the seed of `randomAvatar`. */
function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** A silhouette picked by `seed`: the same seed, the same silhouette, from what is free. */
export function randomAvatar(seed: string): Avatar {
  let state = hash(seed) || 1;
  const next = (): number => {
    // xorshift32
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state;
  };
  const pick = <T>(list: readonly T[]): T => list[next() % list.length] as T;
  return {
    hat: pick(AVATAR_FREE.hat),
    mask: pick(AVATAR_FREE.mask),
    coat: pick(AVATAR_FREE.coat),
    rim: pick(AVATAR_FREE.rim),
    suit: pick(AVATAR_FREE.suit),
  };
}
